/*
  Vitals — Thai province canonicalisation.

  Turns whatever Google's Geocoding API calls a province into the exact Latin name Shopify has in its
  Thailand zone list. Shopify matches a Thai address on that name; a province it can't match is worse
  than an empty one, because it can file the address under the wrong zone without complaining.

  Google's name for the same province is NOT stable, in three separate ways:

    1. Prefixed. `administrative_area_level_1` comes back as "Chang Wat Chiang Mai" or
       "Chang Wat Khon Kaen" — the romanised จังหวัด, as TWO words. (The previous single-word
       `Changwat` strip never matched, so those names reached Shopify intact and unmatchable.)
    2. Spaced differently. "Chonburi" vs "Chon Buri", "Buriram" vs "Buri Ram", "Lop Buri" vs
       "Lopburi" — so matching has to ignore spaces and hyphens entirely.
    3. Still Thai script. `language=en` only romanises where Google has a transliteration; for much
       of the country it returns "เชียงใหม่" regardless. Every province therefore carries its Thai
       name as an alias rather than being dropped.

  The Latin names are the ones Shopify expects — kept in sync with the mobile app's
  `src/modules/Account/constants/thaiProvinces.ts`, which needs the same list for its address form.
  Ordered by ISO 3166-2:TH code so the two files can be diffed against each other.

  Usage:
    VitalsThaiProvinces.canonical('Chang Wat Chiang Mai');   // "Chiang Mai"
    VitalsThaiProvinces.canonical('เชียงใหม่');                // "Chiang Mai"
    VitalsThaiProvinces.canonical('Atlantis');               // "" — no match, send nothing
    VitalsThaiProvinces.cleanLocality('Amphoe Mueang Khon Kaen');  // "Khon Kaen"
    VitalsThaiProvinces.zoneCode('Chonburi');                // "TH-20" — for zoneCode on the
                                                             //   Customer Account API
*/
(function () {
  'use strict';

  if (window.VitalsThaiProvinces) return;

  /** `[Shopify's Latin name, Thai name, Shopify zone code]`, ordered by code (TH-10 … TH-96). */
  var PROVINCES = [
    ['Bangkok', 'กรุงเทพมหานคร', 'TH-10'],
    ['Samut Prakan', 'สมุทรปราการ', 'TH-11'],
    ['Nonthaburi', 'นนทบุรี', 'TH-12'],
    ['Pathum Thani', 'ปทุมธานี', 'TH-13'],
    ['Phra Nakhon Si Ayutthaya', 'พระนครศรีอยุธยา', 'TH-14'],
    ['Ang Thong', 'อ่างทอง', 'TH-15'],
    ['Lopburi', 'ลพบุรี', 'TH-16'],
    ['Sing Buri', 'สิงห์บุรี', 'TH-17'],
    ['Chai Nat', 'ชัยนาท', 'TH-18'],
    ['Saraburi', 'สระบุรี', 'TH-19'],
    ['Chon Buri', 'ชลบุรี', 'TH-20'],
    ['Rayong', 'ระยอง', 'TH-21'],
    ['Chanthaburi', 'จันทบุรี', 'TH-22'],
    ['Trat', 'ตราด', 'TH-23'],
    ['Chachoengsao', 'ฉะเชิงเทรา', 'TH-24'],
    ['Prachin Buri', 'ปราจีนบุรี', 'TH-25'],
    ['Nakhon Nayok', 'นครนายก', 'TH-26'],
    ['Sa Kaeo', 'สระแก้ว', 'TH-27'],
    ['Nakhon Ratchasima', 'นครราชสีมา', 'TH-30'],
    ['Buri Ram', 'บุรีรัมย์', 'TH-31'],
    ['Surin', 'สุรินทร์', 'TH-32'],
    ['Si Sa Ket', 'ศรีสะเกษ', 'TH-33'],
    ['Ubon Ratchathani', 'อุบลราชธานี', 'TH-34'],
    ['Yasothon', 'ยโสธร', 'TH-35'],
    ['Chaiyaphum', 'ชัยภูมิ', 'TH-36'],
    ['Amnat Charoen', 'อำนาจเจริญ', 'TH-37'],
    ['Bueng Kan', 'บึงกาฬ', 'TH-38'],
    ['Nong Bua Lam Phu', 'หนองบัวลำภู', 'TH-39'],
    ['Khon Kaen', 'ขอนแก่น', 'TH-40'],
    ['Udon Thani', 'อุดรธานี', 'TH-41'],
    ['Loei', 'เลย', 'TH-42'],
    ['Nong Khai', 'หนองคาย', 'TH-43'],
    ['Maha Sarakham', 'มหาสารคาม', 'TH-44'],
    ['Roi Et', 'ร้อยเอ็ด', 'TH-45'],
    ['Kalasin', 'กาฬสินธุ์', 'TH-46'],
    ['Sakon Nakhon', 'สกลนคร', 'TH-47'],
    ['Nakhon Phanom', 'นครพนม', 'TH-48'],
    ['Mukdahan', 'มุกดาหาร', 'TH-49'],
    ['Chiang Mai', 'เชียงใหม่', 'TH-50'],
    ['Lamphun', 'ลำพูน', 'TH-51'],
    ['Lampang', 'ลำปาง', 'TH-52'],
    ['Uttaradit', 'อุตรดิตถ์', 'TH-53'],
    ['Phrae', 'แพร่', 'TH-54'],
    ['Nan', 'น่าน', 'TH-55'],
    ['Phayao', 'พะเยา', 'TH-56'],
    ['Chiang Rai', 'เชียงราย', 'TH-57'],
    ['Mae Hong Son', 'แม่ฮ่องสอน', 'TH-58'],
    ['Nakhon Sawan', 'นครสวรรค์', 'TH-60'],
    ['Uthai Thani', 'อุทัยธานี', 'TH-61'],
    ['Kamphaeng Phet', 'กำแพงเพชร', 'TH-62'],
    ['Tak', 'ตาก', 'TH-63'],
    ['Sukhothai', 'สุโขทัย', 'TH-64'],
    ['Phitsanulok', 'พิษณุโลก', 'TH-65'],
    ['Phichit', 'พิจิตร', 'TH-66'],
    ['Phetchabun', 'เพชรบูรณ์', 'TH-67'],
    ['Ratchaburi', 'ราชบุรี', 'TH-70'],
    ['Kanchanaburi', 'กาญจนบุรี', 'TH-71'],
    ['Suphan Buri', 'สุพรรณบุรี', 'TH-72'],
    ['Nakhon Pathom', 'นครปฐม', 'TH-73'],
    ['Samut Sakhon', 'สมุทรสาคร', 'TH-74'],
    ['Samut Songkhram', 'สมุทรสงคราม', 'TH-75'],
    ['Phetchaburi', 'เพชรบุรี', 'TH-76'],
    ['Prachuap Khiri Khan', 'ประจวบคีรีขันธ์', 'TH-77'],
    ['Nakhon Si Thammarat', 'นครศรีธรรมราช', 'TH-80'],
    ['Krabi', 'กระบี่', 'TH-81'],
    ['Phang Nga', 'พังงา', 'TH-82'],
    ['Phuket', 'ภูเก็ต', 'TH-83'],
    ['Surat Thani', 'สุราษฎร์ธานี', 'TH-84'],
    ['Ranong', 'ระนอง', 'TH-85'],
    ['Chumphon', 'ชุมพร', 'TH-86'],
    ['Songkhla', 'สงขลา', 'TH-90'],
    ['Satun', 'สตูล', 'TH-91'],
    ['Trang', 'ตรัง', 'TH-92'],
    ['Phatthalung', 'พัทลุง', 'TH-93'],
    ['Pattani', 'ปัตตานี', 'TH-94'],
    ['Yala', 'ยะลา', 'TH-95'],
    ['Narathiwat', 'นราธิวาส', 'TH-96']
  ];

  /**
   * Names that survive prefix-stripping and space-folding but still don't reach the right province —
   * Bangkok's several romanisations, and the short form of Ayutthaya that everyone actually uses.
   */
  var EXTRA_ALIASES = [
    ['Krung Thep Maha Nakhon', 'Bangkok'],
    ['Krung Thep', 'Bangkok'],
    ['Bangkok Metropolis', 'Bangkok'],
    ['Bangkok Metropolitan Region', 'Bangkok'],
    ['Ayutthaya', 'Phra Nakhon Si Ayutthaya'],
    ['Ayudhya', 'Phra Nakhon Si Ayutthaya']
  ];

  /**
   * Lowercase and drop only separators — spaces, hyphens, apostrophes, dots. Deliberately NOT
   * `[^a-z0-9]`, which would erase Thai script entirely and make every Thai alias collide on "".
   */
  function fold(value) {
    return String(value).toLowerCase().replace(/[\s\-'.]/g, '');
  }

  /**
   * Strip the administrative words Google prefixes or suffixes onto a province name. `Chang\s*Wat`
   * covers both the one-word and two-word romanisations; requiring whitespace after it keeps it from
   * biting into a real name (there is no province whose name starts with "Changwat").
   */
  function stripProvinceWords(value) {
    return String(value || '')
      .replace(/^\s*จังหวัด\s*/, '')
      .replace(/^\s*(?:chang\s*wat|province\s+of)\s+/i, '')
      .replace(/\s+province\s*$/i, '')
      .trim();
  }

  var INDEX = {};
  var CODES = {};
  var i;
  for (i = 0; i < PROVINCES.length; i++) {
    INDEX[fold(PROVINCES[i][0])] = PROVINCES[i][0];
    INDEX[fold(PROVINCES[i][1])] = PROVINCES[i][0];
    CODES[PROVINCES[i][0]] = PROVINCES[i][2];
  }
  for (i = 0; i < EXTRA_ALIASES.length; i++) {
    INDEX[fold(EXTRA_ALIASES[i][0])] = EXTRA_ALIASES[i][1];
  }

  /**
   * Shopify's name for this province, or `''` when it isn't one. An empty string is a real answer:
   * the callers leave the province off the address rather than sending something unmatchable.
   */
  function canonical(raw) {
    var value = stripProvinceWords(raw);
    if (!value) return '';
    return INDEX[fold(value)] || '';
  }

  /**
   * Tidy a Thai city / district line. Google labels the same administrative level three different
   * ways depending on where you are — "Amphoe Mueang Khon Kaen", "Mueang Phuket District",
   * "Nong Chaeng Sub-district", "เขตห้วยขวาง" — and none of that belongs in a City field.
   *
   * Thai script is left alone once the prefix is off: `city` is free text in Shopify, and a Thai
   * buyer reading a Thai storefront would rather see "ห้วยขวาง" than a romanisation. Only `province`
   * has to be Latin, because only `province` is matched against a list.
   *
   * "Mueang" (เมือง, "the capital district of") is dropped only when something follows it, in both
   * scripts — Google returns "Amphoe Mueang Chiang Mai" and "อำเภอเมืองเชียงใหม่" for the same
   * district, and "Chiang Mai" is what a buyer would write. Every province has exactly one เมือง
   * capital district, so this is right far more often than it is wrong; the handful of places
   * genuinely named Mueang-something lose a word in a field Shopify treats as free text.
   */
  function cleanLocality(raw) {
    return String(raw || '')
      .replace(/^\s*(?:amphoe|khet|tambon|khwaeng)\s+/i, '')
      .replace(/^\s*(?:อำเภอ|เขต|ตำบล|แขวง)\s*/, '')
      .replace(/\s*(?:sub-?district|district|province)\s*$/i, '')
      .replace(/^\s*(?:mueang|muang)\s+(?=\S)/i, '')
      .replace(/^\s*เมือง(?=.)/, '')
      .trim();
  }

  /**
   * Shopify's zone code for this province (`'TH-20'`), or `''` when the name isn't one.
   *
   * The Customer Account API's `CustomerAddressInput` takes `zoneCode`, not a province name, and a
   * Thai address without one is rejected outright with "Zone code must exist" — so every caller
   * that writes an address has to come through here first. Already-code-shaped input passes back
   * through unchanged, so a stored code can be re-submitted without a round trip via the name.
   */
  function zoneCode(raw) {
    var value = String(raw || '').trim();
    if (!value) return '';
    if (/^TH-(?:[0-9]{2}|S)$/i.test(value)) return value.toUpperCase();
    var name = canonical(value);
    return (name && CODES[name]) || '';
  }

  /** Shopify's province names, in ISO-code order — for populating a picker or datalist. */
  function names() {
    return PROVINCES.map(function (p) { return p[0]; });
  }

  window.VitalsThaiProvinces = {
    canonical: canonical,
    cleanLocality: cleanLocality,
    zoneCode: zoneCode,
    names: names
  };
})();
